import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Empty, ErrorMsg, Loading, Modal, Section } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import type { PublicationKind, PublicationRow, PublicationStatus } from '../../lib/contentTypes';

export const KIND_LABEL: Record<PublicationKind, string> = {
  post: 'Post',
  carousel: 'Carousel',
  short: 'Short',
  reel: 'Reel',
  story: 'Story',
  video: 'Video',
  article: 'Article',
  other: 'Other',
};
export const PUB_STATUS_LABEL: Record<PublicationStatus, string> = {
  planned: 'planned',
  in_progress: 'in progress',
  ready: 'ready',
  published: 'published',
};
const PUB_TONE: Record<PublicationStatus, string> = { planned: 'idle', in_progress: 'warn', ready: 'ok', published: 'live' };

export function PubStatusBadge({ status }: { status: PublicationStatus }) {
  return (
    <span className={`cs-status tone-${PUB_TONE[status]}`} data-status={status}>
      <span className={`sq ${status === 'planned' ? 'outline' : 'filled'} tone-${PUB_TONE[status]}`} aria-hidden="true" /> {PUB_STATUS_LABEL[status]}
    </span>
  );
}

/** Copies text to the clipboard; falls back to a hidden textarea where the Clipboard API is unavailable (plain http). */
export async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    ta.remove();
    return ok;
  }
}

export function CopyButton({ value, label, className = 'btn small' }: { value: string; label: string; className?: string }) {
  const [state, setState] = useState<'' | 'ok' | 'fail'>('');
  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() =>
          void copyText(value).then((ok) => {
            setState(ok ? 'ok' : 'fail');
            setTimeout(() => setState(''), 2500);
          })
        }
      >
        {label}
      </button>
      <span className="small" role="status" aria-live="polite">
        {state === 'ok' ? 'Copied' : state === 'fail' ? 'Could not copy: select it by hand' : ''}
      </span>
    </>
  );
}

function PublicationTable({ items, showProject }: { items: PublicationRow[]; showProject: boolean }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Publication</th>
            {showProject && <th>Project</th>}
            <th>Kind</th>
            <th>Status</th>
            <th>Planned for</th>
            <th>Texts</th>
            <th>Files</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id}>
              <td>
                <Link to={`/content/publications/${p.id}`}>{p.title}</Link>
              </td>
              {showProject && (
                <td>
                  <Link to={`/content/projects/${p.project_id}`}>{p.project_title}</Link>
                </td>
              )}
              <td>{KIND_LABEL[p.kind]}</td>
              <td>
                <PubStatusBadge status={p.status} />
              </td>
              <td className="num">{p.planned_for ?? <span className="muted">not planned</span>}</td>
              <td className="num">{p.text_count}</td>
              <td className="num">{p.file_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The "Publications" tab of Content Studio: every publication, by planned date. */
export function PublicationsTab() {
  const [status, setStatus] = useState('');
  const [data, setData] = useState<{ items: PublicationRow[]; counts: Record<string, number> } | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api<{ items: PublicationRow[]; counts: Record<string, number> }>(`/api/content/publications${status ? `?status=${status}` : ''}`)
      .then(setData)
      .catch((e) => setError(errorText(e)));
  }, [status]);
  if (error) return <ErrorMsg>{error}</ErrorMsg>;
  if (!data) return <Loading what="Loading publications" />;
  return (
    <section className="section">
      <div className="row gap wrap center cs-filter">
        <label className="field cs-inline">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter publications by status">
            <option value="">all</option>
            {(Object.keys(PUB_STATUS_LABEL) as PublicationStatus[]).map((s) => (
              <option key={s} value={s}>
                {PUB_STATUS_LABEL[s]} ({data.counts[s] ?? 0})
              </option>
            ))}
          </select>
        </label>
        <span className="muted small">Sorted by planned date; unplanned ones last. A publication is created on its project page.</span>
      </div>
      {data.items.length === 0 ? (
        <Empty>No publications{status ? ` with status ${PUB_STATUS_LABEL[status as PublicationStatus]}` : ''} yet. Open a project and use “New publication”.</Empty>
      ) : (
        <PublicationTable items={data.items} showProject />
      )}
    </section>
  );
}

/** The "Publications" section of a project page. */
export function ProjectPublications({ projectId }: { projectId: string }) {
  const [items, setItems] = useState<PublicationRow[] | null>(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    api<{ items: PublicationRow[] }>(`/api/content/publications?project_id=${projectId}`)
      .then((d) => setItems(d.items))
      .catch((e) => setError(errorText(e)));
  }, [projectId]);
  useEffect(load, [load]);
  return (
    <Section
      title={`Publications${items ? ` (${items.length})` : ''}`}
      id="publications"
      aside={
        <button type="button" className="btn small primary" onClick={() => setOpen(true)}>
          New publication
        </button>
      }
    >
      <ErrorMsg>{error}</ErrorMsg>
      {!items ? (
        <Loading what="Loading publications" />
      ) : items.length === 0 ? (
        <Empty>No publication yet. One project can have several: a post, a carousel, shorts. Each keeps its output folder, files and texts per account.</Empty>
      ) : (
        <PublicationTable items={items} showProject={false} />
      )}
      {open && <NewPublicationDialog projectId={projectId} onClose={() => setOpen(false)} />}
    </Section>
  );
}

function NewPublicationDialog({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const navigate = useNavigate();
  const [f, setF] = useState({ title: '', kind: 'post', planned_for: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const p = await api<PublicationRow>('/api/content/publications', {
        method: 'POST',
        body: { project_id: projectId, title: f.title, kind: f.kind, planned_for: f.planned_for || null },
      });
      navigate(`/content/publications/${p.id}`);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }
  return (
    <Modal
      title="New publication"
      onClose={onClose}
      footer={
        <button type="submit" form="new-publication" className="btn primary" disabled={busy || !f.title.trim()}>
          Create publication
        </button>
      }
    >
      <form id="new-publication" className="stack" onSubmit={submit} aria-label="New publication">
        <label className="field">
          <span>Title</span>
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required autoFocus maxLength={300} />
        </label>
        <label className="field">
          <span>Kind</span>
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
            {(Object.keys(KIND_LABEL) as PublicationKind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Planned for (optional)</span>
          <input type="date" value={f.planned_for} onChange={(e) => setF({ ...f, planned_for: e.target.value })} />
        </label>
        <p className="muted small">The output folder, files and the texts per account are on the publication page.</p>
        <ErrorMsg>{error}</ErrorMsg>
      </form>
    </Modal>
  );
}
